import { parseNumericColumn } from "./parseNumericColumn";

const ZIP_VERSION = 20;
const XML_ENCODER = new TextEncoder();
const INVALID_XML_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uD800-\uDFFF\uFFFE\uFFFF]/g;

type ZipEntry = {
  name: string;
  data: Uint8Array;
};

const CRC32_LOOKUP = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < table.length; i++) {
    let crc = i;
    for (let bit = 0; bit < 8; bit++) {
      const shouldXor = (crc & 1) !== 0;
      crc = crc >>> 1;
      if (shouldXor) {
        crc ^= 0xedb88320;
      }
    }
    table[i] = crc >>> 0;
  }
  return table;
})();

export type WorkbookCell = string | number | null | undefined;
export type WorkbookRow = WorkbookCell[];

export interface WorkbookOptions {
  /**
   * 1-based row number holding the table header. That row is written bold and gets Excel's
   * filter dropdowns. Omit when the sheet has no header row.
   */
  headerRow?: number;
  /**
   * 1-based column numbers to write as real numbers rather than text, so Excel can sum and
   * sort them. Anything else stays text — a serial number or a licence plate must not lose its
   * leading zeros to a helpful guess.
   */
  numericColumns?: number[];
  /**
   * 1-based row number the table data starts at. Defaults to the row after `headerRow`, which is
   * right whenever there is one. Set it when the sheet carries a preamble but no header row:
   * without it the preamble is read as data, and a line such as "Vehicles | 1234" becomes a
   * right-aligned, thousands-grouped number.
   */
  firstDataRow?: number;
}

/**
 * Style ids as written into `xl/styles.xml`; 0 is the default and is left off the cell.
 *
 * There is no bold-number style: only data rows are read as numbers, and the header row is by
 * definition not one of them.
 */
const STYLE_DEFAULT = 0;
const STYLE_BOLD = 1;
const STYLE_NUMBER = 2;

/**
 * Thousands-grouped with up to three decimals. The separators in an OOXML format code are
 * invariant (`,` groups, `.` separates decimals) and Excel renders them in the reader's own
 * locale — so this must not be localised here, or every reader would see German separators.
 */
const NUMBER_FORMAT_ID = 164;
const NUMBER_FORMAT_CODE = "#,##0.###";

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 80;

/**
 * The XML parts of the workbook, in package order.
 *
 * Exported for tests: a malformed or inconsistent part — a style id the stylesheet does not
 * declare, a relationship pointing nowhere — makes Excel refuse the whole file, and that is not
 * something the caller finds out about until a user tries to open it.
 */
export function buildWorkbookParts(
  rows: WorkbookRow[],
  options: WorkbookOptions = {},
): { name: string; xml: string }[] {
  return [
    { name: "[Content_Types].xml", xml: getContentTypesXml() },
    { name: "_rels/.rels", xml: getRootRelationshipsXml() },
    { name: "xl/workbook.xml", xml: getWorkbookXml() },
    { name: "xl/_rels/workbook.xml.rels", xml: getWorkbookRelationshipsXml() },
    { name: "xl/styles.xml", xml: getStylesXml() },
    { name: "xl/worksheets/sheet1.xml", xml: buildSheetXml(rows, options) },
  ];
}

export function createWorkbook(rows: WorkbookRow[], options: WorkbookOptions = {}): Blob {
  const files = buildWorkbookParts(rows, options).map(part => ({
    name: part.name,
    data: XML_ENCODER.encode(part.xml),
  }));

  const zipped = createZip(files);
  return new Blob([zipped], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function getContentTypesXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>',
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>',
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
    "</Types>",
  ].join("");
}

function getRootRelationshipsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>',
    "</Relationships>",
  ].join("");
}

function getWorkbookXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ',
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    "<sheets>",
    '<sheet name="Sheet1" sheetId="1" r:id="rId1"/>',
    "</sheets>",
    "</workbook>",
  ].join("");
}

function getWorkbookRelationshipsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>',
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>',
    "</Relationships>",
  ].join("");
}

function buildSheetXml(rows: WorkbookRow[], options: WorkbookOptions = {}) {
  const maxColumns = getMaxColumnCount(rows);
  const rowCount = Math.max(1, rows.length);
  const endCellRef = `${columnNumberToName(maxColumns)}${rowCount}`;
  const headerRowIndex = options.headerRow === undefined ? -1 : options.headerRow - 1;

  // Anything above the first data row is a label, never a measurement — a preamble line
  // "Fahrzeuge | 1234" must not turn into a right-aligned, thousands-grouped number.
  const firstDataRowIndex =
    options.firstDataRow !== undefined ? options.firstDataRow - 1 : headerRowIndex + 1;
  const numericValuesByColumn = parseNumericColumns(rows, firstDataRowIndex, options.numericColumns);

  const rowXml = rows
    .map((row, rowIndex) => {
      const isHeaderRow = rowIndex === headerRowIndex;
      const cells = row
        .map((rawValue, columnIndex) => {
          const cellRef = `${columnNumberToName(columnIndex + 1)}${rowIndex + 1}`;
          const numericValue =
            numericValuesByColumn.get(columnIndex)?.[rowIndex - firstDataRowIndex] ?? null;

          if (numericValue !== null) {
            // Excel right-aligns numeric cells itself, so no alignment style is needed.
            return `<c r="${cellRef}" s="${STYLE_NUMBER}" t="n"><v>${numericValue}</v></c>`;
          }

          const style = isHeaderRow ? ` s="${STYLE_BOLD}"` : "";
          const cellValue = escapeXml(String(rawValue ?? ""));
          return `<c r="${cellRef}"${style} t="inlineStr"><is><t xml:space="preserve">${cellValue}</t></is></c>`;
        })
        .join("");

      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  // Widths are unconditional: a column sized to its content is always more readable than the
  // default, and no caller has ever wanted the alternative.
  const colsXml = `<cols>${getColumnWidthsXml(rows, maxColumns)}</cols>`;

  // The format requires autoFilter after sheetData. A filter needs a header row to label its
  // dropdowns, and a header row outside the sheet would produce an inverted range such as
  // "A3:B1", which makes Excel declare the file unreadable.
  const headerRow = options.headerRow;
  const hasUsableHeaderRow = headerRow !== undefined && headerRow >= 1 && headerRow <= rowCount;
  const autoFilterXml = hasUsableHeaderRow
    ? `<autoFilter ref="A${headerRow}:${columnNumberToName(maxColumns)}${rowCount}"/>`
    : "";

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    `<dimension ref="A1:${endCellRef}"/>`,
    '<sheetViews><sheetView workbookViewId="0"/></sheetViews>',
    '<sheetFormatPr defaultRowHeight="15"/>',
    colsXml,
    `<sheetData>${rowXml}</sheetData>`,
    autoFilterXml,
    "</worksheet>",
  ].join("");
}

/**
 * Reads each declared numeric column, from the first data row onwards.
 *
 * Whole columns rather than single cells, so one convention applies throughout — see
 * {@link parseNumericColumn}.
 *
 * @returns Column index to the parsed values, indexed from the first data row.
 */
function parseNumericColumns(
  rows: WorkbookRow[],
  firstDataRowIndex: number,
  numericColumns: number[] | undefined,
): Map<number, (number | null)[]> {
  const parsed = new Map<number, (number | null)[]>();
  if (numericColumns === undefined || numericColumns.length === 0) {
    return parsed;
  }

  const columnIndexes = numericColumns.map(column => column - 1).filter(index => index >= 0);
  const columnValues = columnIndexes.map<WorkbookCell[]>(() => []);

  // One sweep over the rows collecting every numeric column at once. Slicing the rows and then
  // mapping once per column allocated a full-length array per column — fifteen numeric columns
  // of a 200 000-row export meant fifteen throwaway arrays before any parsing started.
  const start = Math.max(0, firstDataRowIndex);
  for (let rowIndex = start; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    for (let column = 0; column < columnIndexes.length; column++) {
      columnValues[column].push(row[columnIndexes[column]]);
    }
  }

  columnIndexes.forEach((columnIndex, column) => {
    parsed.set(columnIndex, parseNumericColumn(columnValues[column]));
  });

  return parsed;
}

/**
 * Counted with a loop rather than `Math.max(...rows.map(…))`: the spread passes one argument per
 * row and throws once a large export exceeds the argument limit. A loop also allocates nothing,
 * which `lodash`'s `max` would not avoid — it still needs the intermediate array of lengths.
 */
function getMaxColumnCount(rows: WorkbookRow[]) {
  let maxColumns = 1;
  for (const row of rows) {
    if (row.length > maxColumns) {
      maxColumns = row.length;
    }
  }
  return maxColumns;
}

function getColumnWidthsXml(rows: WorkbookRow[], columnCount: number) {
  const widest = new Array<number>(columnCount).fill(0);

  // One pass over the rows rather than one pass per column, and no spread — an export of a
  // large fleet has more rows than a spread argument list can hold.
  for (const row of rows) {
    const columns = Math.min(row.length, columnCount);
    for (let columnIndex = 0; columnIndex < columns; columnIndex++) {
      const length = String(row[columnIndex] ?? "").length;
      if (length > widest[columnIndex]) {
        widest[columnIndex] = length;
      }
    }
  }

  return widest
    .map((length, columnIndex) => {
      const width = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, length + 2));
      const columnNumber = columnIndex + 1;
      return `<col min="${columnNumber}" max="${columnNumber}" width="${width}" bestFit="1" customWidth="1"/>`;
    })
    .join("");
}

function getStylesXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    `<numFmts count="1"><numFmt numFmtId="${NUMBER_FORMAT_ID}" formatCode="${escapeXml(NUMBER_FORMAT_CODE)}"/></numFmts>`,
    '<fonts count="2">',
    '<font><sz val="11"/><name val="Calibri"/></font>',
    '<font><b/><sz val="11"/><name val="Calibri"/></font>',
    "</fonts>",
    '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>',
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>',
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>',
    '<cellXfs count="3">',
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="${STYLE_DEFAULT}"/>`,
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>',
    `<xf numFmtId="${NUMBER_FORMAT_ID}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`,
    "</cellXfs>",
    "</styleSheet>",
  ].join("");
}

function escapeXml(value: string) {
  return value
    .replace(INVALID_XML_CHARACTERS, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function columnNumberToName(value: number) {
  let column = "";
  let current = value;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    current = Math.floor((current - 1) / 26);
  }

  return column;
}

function createZip(entries: ZipEntry[]) {
  const localFileParts: Uint8Array[] = [];
  const centralDirectoryParts: Uint8Array[] = [];
  let fileOffset = 0;

  for (const entry of entries) {
    const fileNameBytes = XML_ENCODER.encode(entry.name);
    const data = entry.data;
    const crc = crc32(data);

    const localHeader = new Uint8Array(30 + fileNameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, ZIP_VERSION, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, fileNameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(fileNameBytes, 30);

    const centralHeader = new Uint8Array(46 + fileNameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, ZIP_VERSION, true);
    centralView.setUint16(6, ZIP_VERSION, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, fileNameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, fileOffset, true);
    centralHeader.set(fileNameBytes, 46);

    localFileParts.push(localHeader, data);
    centralDirectoryParts.push(centralHeader);

    fileOffset += localHeader.length + data.length;
  }

  const centralDirectoryOffset = fileOffset;
  const centralDirectory = concatUint8Arrays(centralDirectoryParts);
  const endOfCentralDirectory = new Uint8Array(22);
  const eocdView = new DataView(endOfCentralDirectory.buffer);
  eocdView.setUint32(0, 0x06054b50, true);
  eocdView.setUint16(4, 0, true);
  eocdView.setUint16(6, 0, true);
  eocdView.setUint16(8, entries.length, true);
  eocdView.setUint16(10, entries.length, true);
  eocdView.setUint32(12, centralDirectory.length, true);
  eocdView.setUint32(16, centralDirectoryOffset, true);
  eocdView.setUint16(20, 0, true);

  return concatUint8Arrays([...localFileParts, centralDirectory, endOfCentralDirectory]);
}

function concatUint8Arrays(arrays: Uint8Array[]) {
  const totalLength = arrays.reduce((sum, array) => sum + array.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  for (const array of arrays) {
    merged.set(array, offset);
    offset += array.length;
  }

  return merged;
}

function crc32(data: Uint8Array) {
  let crc = 0xffffffff;
  for (const value of data) {
    const index = (crc ^ value) & 0xff;
    crc = (crc >>> 8) ^ CRC32_LOOKUP[index];
  }
  return (crc ^ 0xffffffff) >>> 0;
}
